function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch {
    return null;
  }
}

export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  const registration = await registerServiceWorker();
  if (!registration || !vapidPublicKey) return null;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  const registration = await registerServiceWorker();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

export async function unsubscribeFromPush(): Promise<boolean> {
  const subscription = await getExistingPushSubscription();
  if (!subscription) return true;
  return subscription.unsubscribe();
}

export async function cleanupPushOnLogout(): Promise<void> {
  try {
    const existing = await getExistingPushSubscription();
    const { apolloClient } = await import("../../apollo/graphql");
    const { REMOVE_PUSH_SUBSCRIPTION } = await import("../../apollo/mutations");
    await apolloClient.mutate({
      mutation: REMOVE_PUSH_SUBSCRIPTION,
      variables: { endpoint: existing?.endpoint ?? null },
    });
    await unsubscribeFromPush();
  } catch {
    // ignore cleanup errors on logout
  }
}

export function subscriptionToInput(subscription: PushSubscription) {
  const json = subscription.toJSON();
  const keys = json.keys as { p256dh?: string; auth?: string };
  return {
    endpoint: json.endpoint as string,
    p256dh: keys.p256dh as string,
    auth: keys.auth as string,
  };
}
